import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  output,
  signal,
} from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { IconButton } from '../icon-button/icon-button';
import { NotificationsButton } from '../notifications-button/notifications-button';
import { AlertBell } from '../alert-bell/alert-bell';
import { Weather } from '../weather/weather';
import { WeatherService } from '../weather/weather.service';
import { PortalService } from '../../../core/portal.service';

/**
 * StatusToolbar — the right-hand cluster of the top bar: notifications, the
 * current date/time, the weather chip and a search control. Designed to sit
 * on the dark header, so it paints white (inherits `currentColor`).
 *
 * Reality note: date/time is a real client clock and weather is live
 * OpenWeather data (hidden when no API key is configured). In the tenant
 * portal the bell is the live alerts bell (tenant-scoped API); the admin
 * back-office keeps an inert bell since there is no cross-tenant alert feed
 * yet. Search has no backend — the button just emits `searchClicked`.
 */
@Component({
  selector: 'app-status-toolbar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoDirective, IconButton, NotificationsButton, AlertBell, Weather],
  templateUrl: './status-toolbar.html',
})
export class StatusToolbar {
  private readonly destroyRef = inject(DestroyRef);
  protected readonly weather = inject(WeatherService);
  protected readonly inAdminPortal = inject(PortalService).inAdminPortal;

  readonly notificationsClicked = output<void>();
  readonly searchClicked = output<void>();

  // Live clock, refreshed each minute.
  protected readonly now = signal(this.formatNow());

  constructor() {
    const id = setInterval(() => this.now.set(this.formatNow()), 60_000);
    this.destroyRef.onDestroy(() => clearInterval(id));
  }

  private formatNow(): string {
    const d = new Date();
    const day = d.toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
    const time = d.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
    return `${day} · ${time}`;
  }
}
